import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthorizationManager from '../../services/auth/AuthorizationManager';
import useQueryParams from '../../hooks/utility/useQueryParams';
import BackendApiClient from '../../services/api/BackendApiClient';
import { RouteEnum } from '../../routes/routes';
import { useAuth } from '../../providers/AuthProvider';
import { useBrand } from '../../providers/BrandProvider';
import { Button, Form, Input, Spin } from 'antd';

export default function LoginView() {
  const { t } = useTranslation(null, { keyPrefix: 'Views:Auth:LoginView' });

  const brand = useBrand();
  const { logIn } = useAuth();
  const navigate = useNavigate();
  const query = useQueryParams();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    query.has('auth_token') && validateToken();
  }, []);

  async function validateToken() {
    setLoading(true);
    const token = query.get('auth_token');
    const { success } = await BackendApiClient.requestAuthorized({
      method: 'GET',
      url: '/staff/profile',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!success) {
      setLoading(false);

      return toast.error(t('toasts.auth_token_invalid'));
    }

    return onLogin(token);
  }

  async function validateCredentials() {
    setLoading(true);
    const { success, data } = await AuthorizationManager.login({
      username,
      password,
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.credentials_invalid'));
    }

    return onLogin(data.auth_token);
  }

  async function onLogin(token) {
    logIn({ token });

    setTimeout(() => navigate(RouteEnum.Home), 5);
  }

  function renderBrandHeader() {
    if (!brand?.isWhiteLabel) {
      return null;
    }

    return (
      <div className="flex items-center justify-center gap-2 mb-md">
        {brand.logoUrl && <img src={brand.logoUrl} alt={brand.name} className="h-10 object-contain" />}
        <span className="text-lg font-semibold">{brand.name}</span>
      </div>
    );
  }

  // Never paint the wrong brand: wait for /brand-config (or the host rule) to settle.
  if (!brand?.settled) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen flex flex-row items-center">
      <div className="w-full h-full m-auto flex justify-center px-md lg:px-[60px]">
        <div className="lg:w-1/2">
          {renderBrandHeader()}
          <h3 className="text-center mb-md">{t('title')}</h3>
          <Form layout="vertical">
            <Form.Item>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('form.username.placeholder')}
                autoComplete="username"
              />
            </Form.Item>
            <Form.Item>
              <Input.Password
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('form.password.placeholder')}
                autoComplete="current-password"
              />
            </Form.Item>
          </Form>
          <Button type="primary" onClick={validateCredentials} loading={loading} className="w-full">
            {t('buttons.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
